// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useMemo, useRef, useState } from 'react';

import type { ResourceCreate, ReviewStatus, SurveyType } from '../../../api/types';
import type { CaseActions, CaseData } from '../../../app/bygning/useCaseData';
import { useFieldView } from '../../../app/bygning/useFieldView';
import { createOnceGuard } from '../../../app/onceGuard';
import { BUILDUPS, buildupColumnKey, currentBuildup, hasServerMass, legacyColumnBuildup } from '../../../bygning/buildups';
import { fieldGroups, keyForRole, typeValue, yesNo } from '../../../bygning/fields';
import { formatFieldView } from '../../../bygning/fieldView';
import { tonnesFigure } from '../../../bygning/measure';
import { planToast, runMerge } from '../../../bygning/parts';
import { isManualType } from '../../../bygning/provenance';
import { roomFigure, roomShareText } from '../../../bygning/resourceFilter';
import { envBadge, missingBeforeReport, neighbourAfter, reviewPill, stripTypes } from '../../../bygning/review';
import { roomName, roomsOf } from '../../../bygning/rooms';
import type { Selection } from '../../../bygning/selection';
import { invalidValueMessage } from '../../../kortlaegning/writeOutcome';
import { resourceIndex } from '../../../kortlaegning/resources';
import { IconButton, PencilIcon } from '../../IconButton';
import { Pill } from '../../Pill';
import { AddResourceDialog } from '../../kortlaegning/AddResourceDialog';
import { FieldViewSelect } from '../FieldViewSelect';
import { EditTypeDialog } from './EditTypeDialog';
import styles from './Inspector.module.css';
import { PartChips } from './PartChips';
import { PartFocus } from './PartFocus';
import { Portal } from './Portal';
import { ResourceImage } from './ResourceImage';
import { ReviewActions } from './ReviewActions';
import { TypeColorPicker } from './TypeColorPicker';
import { TypeFields } from './TypeFields';
import { useTypeWrites } from './useTypeWrites';

export interface TypeInspectorProps {
  type: SurveyType;
  data: CaseData;
  sel: Selection;
  setSel: (patch: Partial<Selection>) => void;
  actions: CaseActions;
  /** `review`: Gennemgang — no type admin. */
  variant?: 'ressourcer' | 'review';
  /** Godkend / Fejl / back to the queue (the mode moves the selection on). */
  onReview: (status: ReviewStatus) => void;
  /** Open Redigér type straight away (a type just made, to name it). */
  autoRename?: boolean;
  /**
   * The strip the type is listed in (Ressourcer: the filtered set's types),
   * where the selection goes after Slet type (Redigér type); default `stripTypes`.
   */
  strip?: readonly SurveyType[];
  /** Ressourcer: the part last opened from the Dele chips (its room reopens on the way back, `PartChips`). */
  recentPart?: string | null;
}

/**
 * The type inspector (v8 `rightType`): title, review pill and miljøstatus
 * badge, with the edit pencil top-right (Redigér type: Navn, Flet hele typen
 * ind i…, Slet — `EditTypeDialog`), the big mass (under a room focus with the
 * room's share beneath it), "Mangler før rapport", the Dele chips — in
 * Ressourcer a chip opens the part in its own inspector (`PartInspector`, the
 * mode swaps it in); in Gennemgang it picks and focuses, with the evidence
 * buttons and the focused part (`PartFocus`) inline, so Godkend/Fejl stay in
 * reach — the Felter picker (a template or Alle felter, `useFieldView`, shared
 * with Tabel › Typer) with its field groups, and the footer pinned to the foot
 * of the tile (`ReviewActions`: Ressourcer's Markér vigtig star, Fejl,
 * Godkend). Every write goes through `CaseActions`.
 */
export function TypeInspector({
  type,
  data,
  sel,
  setSel,
  actions,
  variant = 'ressourcer',
  onReview,
  autoRename,
  strip,
  recentPart,
}: TypeInspectorProps) {
  const home = useRef<HTMLDivElement>(null);
  const fields = useFieldView(data, sel, setSel);
  const [addOpen, setAddOpen] = useState(false);
  const createGuard = useRef(createOnceGuard());
  const writes = useTypeWrites(data, actions);
  const { canEdit, busy } = data;

  // Redigér type: a type just made opens straight in it; another type closes it.
  const [editOpen, setEditOpen] = useState(Boolean(autoRename) && canEdit);
  useEffect(() => {
    setEditOpen(Boolean(autoRename) && canEdit);
  }, [type.id, autoRename, canEdit]);

  const index = useMemo(() => resourceIndex(data.resources), [data.resources]);
  const catalogue = data.resourceKeys;
  const { view } = fields;
  const groups = useMemo(
    () => fieldGroups(view, data.templates, catalogue, { unit: type.unit }),
    [view, data.templates, catalogue, type.unit],
  );

  const buildupKey = buildupColumnKey(catalogue);
  const buildupValue = buildupKey ? typeValue(type, buildupKey, index) : { value: null, mixed: false };
  // The server's library when it has one (ReUseX schema v37), else the embedded copy.
  const library = data.buildups ?? BUILDUPS;
  const buildup = currentBuildup(type, library, buildupValue.value);
  const legacyBuildup = legacyColumnBuildup(type, library, buildupValue.value);

  const screenKey = keyForRole('screenet', catalogue);
  const screened = screenKey ? yesNo(typeValue(type, screenKey, index).value) === true : null;
  const missing = missingBeforeReport(type, screened);
  const pill = reviewPill(type);
  const env = envBadge(type, data.samples);
  // Only Gennemgang shows the focused part inline; Ressourcer drills down to PartInspector.
  const focusedPart =
    variant === 'review' && sel.partCode !== null ? (type.parts.find((p) => p.code === sel.partCode) ?? null) : null;
  // Gennemgang has no type admin: no Flet there.
  const mergeTargets = variant === 'review' ? [] : data.types.filter((t) => t.id !== type.id && t.review_status !== 'rejected');
  const rooms = useMemo(() => roomsOf(data.types, data.summary), [data.types, data.summary]);
  // Under a room focus: what of the type is in the room (its tonnes estimated where the parts carry none).
  const roomShare =
    sel.room === null ? null : { name: roomName(rooms, sel.room) ?? `Rum ${sel.room}`, figure: roomFigure(type, sel.room) };

  const onInvalid = (label: string) => actions.notify(invalidValueMessage(label));

  async function merge(targetId: number) {
    const target = data.types.find((t) => t.id === targetId);
    if (!target) return;
    const n = type.parts.length;
    const { ok } = await runMerge(type, target, data.samples, actions);
    if (ok) {
      actions.notify(planToast('merge', n, target.name));
      setSel({ typeId: target.id });
    }
  }

  async function deleteType() {
    // Ressourcer's strip is the filtered set's types: the next one comes from there.
    const list = strip ?? stripTypes(data.types);
    const next = neighbourAfter(list, type.id);
    const r = await actions.deleteType(type.id);
    if (r) setSel({ typeId: next });
  }

  function addPart(body: ResourceCreate) {
    createGuard.current.run(async () => {
      const created = await actions.createResource(body);
      if (created) {
        setAddOpen(false);
        setSel({ typeId: created.type_id, partCode: created.code });
      }
    });
  }

  const metaBits = [`#${type.id}`, type.eak_code ? `${type.eak_code}${type.eak_name ? ` ${type.eak_name}` : ''}` : 'ingen EAK'];
  if (type.bim7aa_code) metaBits.push(type.bim7aa_code);
  if (isManualType(type)) metaBits.push('manuel');

  return (
    <div ref={home} className={styles.root} tabIndex={-1}>
      <ResourceImage
        type={type}
        index={index}
        partCode={sel.partCode}
        canEdit={canEdit}
        onOpen={(code) => setSel({ partCode: code, ev: 'foto' })}
        placement="hero"
      />
      <div className={styles.head}>
        <div className={styles.headMain}>
          <h2 className={styles.title}>{type.name}</h2>
          <Pill tone={pill.tone}>{pill.label}</Pill>
          {env && <Pill tone={env.tone}>{env.label}</Pill>}
          {type.starred && (
            <span className={styles.star} role="img" aria-label="Vigtig" title="Vigtig">
              ★
            </span>
          )}
        </div>
        {canEdit && (
          <IconButton variant="ghost" size="sm" label="Redigér type" aria-haspopup="dialog" onClick={() => setEditOpen(true)}>
            <PencilIcon />
          </IconButton>
        )}
      </div>
      <div className={styles.meta}>{metaBits.join(' · ')}</div>

      <div className={styles.big}>
        <small className={styles.bigLabel}>Masse</small>
        <b className={styles.bigFigure}>
          {type.mass_t !== null ? (
            <>
              {tonnesFigure(type.mass_t)}
              <i className={styles.bigUnit}>t</i>
            </>
          ) : (
            <span className={styles.bigMissing}>mangler</span>
          )}
        </b>
        {roomShare && (
          <p
            className={styles.sub}
            title={roomShare.figure.estimated ? '≈ anslået ud fra typens samlede masse, fordelt efter mængde eller antal' : undefined}
          >
            {roomShareText(roomShare.name, roomShare.figure, type.unit)}
          </p>
        )}
      </div>

      <ResourceImage
        type={type}
        index={index}
        partCode={sel.partCode}
        canEdit={canEdit}
        onOpen={(code) => setSel({ partCode: code, ev: 'foto' })}
        placement="section"
      />

      {missing.length > 0 && <div className={styles.missing}>Mangler før rapport: {missing.join(', ')}</div>}

      <PartChips
        type={type}
        sel={sel}
        setSel={setSel}
        canEdit={canEdit}
        onAddPart={() => setAddOpen(true)}
        variant={variant}
        recent={recentPart}
      />

      {focusedPart && (
        <PartFocus
          type={type}
          part={focusedPart}
          resource={index.get(focusedPart.code) ?? null}
          catalogue={catalogue}
          canEdit={canEdit}
          busy={busy}
          home={home}
          onPatch={(patch) => void actions.patchPart(focusedPart.code, patch)}
          onCellCommit={async (keyId, value, label) => {
            await actions.patchResource(focusedPart.code, { [keyId]: value }, label);
          }}
          onInvalid={onInvalid}
          onDelete={() => {
            const code = focusedPart.code;
            void actions.deleteResource(code).then((ok) => {
              if (ok) setSel({ partCode: null, selParts: sel.selParts.filter((c) => c !== code) });
            });
          }}
          onClose={() => setSel({ partCode: null })}
        />
      )}

      <FieldViewSelect
        id={`felter-${variant}`}
        label="Felter"
        block
        className={styles.fieldsRow}
        options={fields.options}
        value={formatFieldView(view)}
        onChange={fields.choose}
      />

      <TypeFields
        type={type}
        types={data.types}
        groups={groups}
        showRefs={view.kind === 'all'}
        index={index}
        catalogue={catalogue}
        samples={data.samples}
        buildup={buildup}
        buildupMixed={!hasServerMass(type) && buildupValue.mixed}
        library={library}
        legacyBuildup={legacyBuildup}
        room={sel.room}
        canEdit={canEdit}
        busy={busy}
        home={home}
        onRoom={(room) => setSel({ room })}
        onSample={(sampleId) => setSel({ mode: 'miljoe', sampleId })}
        onPatchType={(patch) => void actions.patchType(type.id, patch)}
        onCommitKey={(key, value) => writes.commitKey(type, key, value)}
        onInvalid={onInvalid}
        onPickBuildup={(b) => void writes.pickBuildup(type, buildup, b)}
        onSplitLayer={(i) => {
          if (buildup) void writes.splitLayer(type, buildup, i);
        }}
      />

      {variant === 'ressourcer' && (
        <TypeColorPicker
          type={type}
          canEdit={canEdit}
          busy={busy}
          shared={data.typeColorShared}
          onPick={(color) => void actions.setTypeColor(type.id, color)}
        />
      )}

      <div className={styles.spacer} />

      {canEdit && (
        <ReviewActions
          type={type}
          samples={data.samples}
          busy={busy}
          onReview={onReview}
          lead={
            variant === 'ressourcer' && (
              <IconButton
                label={type.starred ? 'Fjern vigtig' : 'Markér vigtig'}
                aria-pressed={type.starred}
                className={styles.starToggle}
                disabled={busy}
                onClick={() => void actions.patchType(type.id, { starred: !type.starred })}
              >
                <span aria-hidden="true">{type.starred ? '★' : '☆'}</span>
              </IconButton>
            )
          }
        />
      )}

      {canEdit && addOpen && (
        <Portal>
          <AddResourceDialog
            types={data.types}
            defaultTypeId={type.id}
            busy={busy}
            onCancel={() => setAddOpen(false)}
            onSubmit={addPart}
          />
        </Portal>
      )}

      {canEdit && editOpen && (
        <Portal>
          <EditTypeDialog
            key={type.id}
            type={type}
            mergeTargets={mergeTargets}
            canDelete={variant === 'ressourcer'}
            busy={busy}
            onCancel={() => setEditOpen(false)}
            onRename={(name) => void actions.patchType(type.id, { name })}
            onMerge={(id) => {
              setEditOpen(false);
              void merge(id);
            }}
            onDelete={() => {
              setEditOpen(false);
              void deleteType();
            }}
          />
        </Portal>
      )}
    </div>
  );
}
